import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import DateRangePickerField, { BlockedDays } from '../components/DateRangePickerField';
import { Add as AddIcon, Check as CheckIcon, Close as CloseIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { workCenterAnnualBagsApi } from '../services/workCenterAnnualBagsApi';
import { getVacationRequests, createVacationRequest, updateVacationRequest, deleteVacationRequest, getWorkers, getVacationTypes, getWorkersVacationSummary, countWorkingDays, getWorkerAvailableHours, getWorkHoursRange, getBlockedDays } from '../services/api';
import { VacationRequest, VacationStatus, Worker, VacationType, WorkerVacationSummary } from '../types';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';

const VacationRequests: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [requests, setRequests] = useState<VacationRequest[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [vacationTypes, setVacationTypes] = useState<VacationType[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([new Date().getFullYear()]);
  const previousYearSummariesRef = useRef<WorkerVacationSummary[]>([]);
  const currentYearSummariesRef = useRef<WorkerVacationSummary[]>([]);
  const availableYearsRef = useRef<number[]>([new Date().getFullYear()]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRequest, setEditingRequest] = useState<VacationRequest | null>(null);
  const [dateError, setDateError] = useState<string>('');
  const [bagError, setBagError] = useState<string>('');
  const [dailyWorkHours, setDailyWorkHours] = useState<number | null>(null);
  const [blockedDays, setBlockedDays] = useState<BlockedDays | undefined>(undefined);
  const [newRequest, setNewRequest] = useState({
    worker_id: '',
    vacation_type_id: '',
    start_date: null as Date | null,
    end_date: null as Date | null,
    hours_requested: '',
    notes: '',
    vacation_bag_year: new Date().getFullYear()
  });

  const getStatusColor = (status: VacationStatus) => {
    switch (status) {
      case VacationStatus.PENDING: return 'warning';
      case VacationStatus.APPROVED: return 'success';
      case VacationStatus.REJECTED: return 'error';
      case VacationStatus.CANCELLED: return 'default';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: VacationStatus) => {
    switch (status) {
      case VacationStatus.PENDING: return 'Pendiente';
      case VacationStatus.APPROVED: return 'Aprobada';
      case VacationStatus.REJECTED: return 'Rechazada';
      case VacationStatus.CANCELLED: return 'Cancelada';
      default: return status;
    }
  };

  const handleOpenCreate = () => {
    setEditingRequest(null);
    setNewRequest({
      worker_id: user ? user.id.toString() : '',
      vacation_type_id: '',
      start_date: null,
      end_date: null,
      hours_requested: '',
      notes: '',
      vacation_bag_year: new Date().getFullYear()
    });
    setOpenDialog(true);
  };

  const handleEdit = (request: VacationRequest) => {
    setEditingRequest(request);
    setNewRequest({
      worker_id: request.worker_id.toString(),
      vacation_type_id: request.vacation_type_id.toString(),
      start_date: new Date(request.start_date),
      end_date: new Date(request.end_date),
      hours_requested: request.hours_requested?.toString() || '',
      notes: request.notes || '',
      vacation_bag_year: new Date(request.start_date).getFullYear()
    });
    setOpenDialog(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Está seguro de que desea eliminar esta solicitud?')) {
      try {
        // Remove from local state immediately so the DataGrid doesn't try to render a deleted row
        setRequests(prev => prev.filter((r: VacationRequest) => r.id !== id));
        await deleteVacationRequest(id);
        // Full refresh to keep all summaries and counts in sync
        await fetchData();
      } catch (error) {
        console.error('Error deleting request:', error);
        alert('Error al eliminar la solicitud');
        // Restore list on error
        await fetchData();
      }
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    ...(isAdmin ? [{
      field: 'worker_id',
      headerName: 'Trabajador',
      width: 150,
      renderCell: (params: any) => {
        const worker = workers.find((w: Worker) => w.id === params.value);
        return worker ? `${worker.name} ${worker.surname}` : 'N/A';
      }
    }] : []),
    {
      field: 'vacation_type_id',
      headerName: 'Tipo',
      width: 120,
      renderCell: (params: any) => {
        const type = vacationTypes.find((vt: VacationType) => vt.id === params.value);
        return type ? type.name : 'N/A';
      }
    },
    { field: 'start_date', headerName: 'Inicio', width: 120 },
    { field: 'end_date', headerName: 'Fin', width: 120 },
    { field: 'hours_requested', headerName: 'Horas', width: 80 },
    {
      field: 'status',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={getStatusLabel(params.value)}
          color={getStatusColor(params.value)}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 180,
      getActions: (params) => {
        const actions = [];
        
        // Editar y eliminar para todas las solicitudes
        actions.push(
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Editar"
            onClick={() => handleEdit(params.row)}
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Eliminar"
            onClick={() => handleDelete(params.row.id)}
          />
        );
        
        // Aprobar/Rechazar solo para administradores y solicitudes pendientes
        if (isAdmin && params.row.status === VacationStatus.PENDING) {
          actions.push(
            <GridActionsCellItem
              icon={<CheckIcon />}
              label="Aprobar"
              onClick={() => handleUpdateStatus(params.row.id, VacationStatus.APPROVED)}
            />,
            <GridActionsCellItem
              icon={<CloseIcon />}
              label="Rechazar"
              onClick={() => handleUpdateStatus(params.row.id, VacationStatus.REJECTED)}
            />
          );
        }
        
        return actions;
      }
    }
  ];

  const fetchData = useCallback(async () => {
      try {
        const currentYear = new Date().getFullYear();
        const [requestsData, workersData, vacationTypesData, annualBagsData, prevYearSummariesData, currYearSummariesData] = await Promise.all([
          getVacationRequests(),
          getWorkers(),
          getVacationTypes(),
          workCenterAnnualBagsApi.getAll(),
          getWorkersVacationSummary(currentYear - 1),
          getWorkersVacationSummary(currentYear)
        ]);
        
        // Filter requests for normal users to show only their own
        const filteredRequests = isAdmin 
          ? requestsData 
          : requestsData.filter((req: VacationRequest) => req.worker_id === user?.id);
        
        setRequests(filteredRequests);
        setWorkers(workersData);
        setVacationTypes(vacationTypesData);
        previousYearSummariesRef.current = prevYearSummariesData;
        console.log('[DEBUG] fetchData — previousYearSummariesRef updated:', prevYearSummariesData);
        currentYearSummariesRef.current = currYearSummariesData;
        
        // Extraer años únicos de las bolsas anuales
        const years = Array.from(new Set(annualBagsData.map((bag: any) => bag.year))).sort((a: number, b: number) => a - b);
        setAvailableYears(years);
        availableYearsRef.current = years;
        
        // For normal users, automatically set their worker_id in newRequest
        if (!isAdmin && user) {
          setNewRequest(prev => ({
            ...prev,
            worker_id: user.id.toString()
          }));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    }, [isAdmin, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load blocked days whenever the dialog opens or the selected worker changes
  useEffect(() => {
    if (!openDialog) {
      setBlockedDays(undefined);
      return;
    }
    const workerId = !isAdmin && user ? user.id : Number(newRequest.worker_id) || null;
    if (!workerId) {
      setBlockedDays(undefined);
      return;
    }
    const currentYear = new Date().getFullYear();
    // Fetch blocked days for the current year + next year carryover period
    getBlockedDays(workerId, currentYear, currentYear + 1)
      .then(data => {
        // If editing, remove the editing request's own days from all status lists
        if (editingRequest) {
          const editStart = new Date(editingRequest.start_date);
          const editEnd = new Date(editingRequest.end_date);
          const editDates = new Set<string>();
          const cur = new Date(editStart);
          while (cur <= editEnd) {
            editDates.add(format(cur, 'yyyy-MM-dd'));
            cur.setDate(cur.getDate() + 1);
          }
          data.pending = data.pending.filter((d: string) => !editDates.has(d));
          data.approved = data.approved.filter((d: string) => !editDates.has(d));
          data.rejected = data.rejected.filter((d: string) => !editDates.has(d));
        }
        setBlockedDays(data);
      })
      .catch(() => setBlockedDays(undefined));
  }, [openDialog, newRequest.worker_id, isAdmin, user, editingRequest]);

  const handleCreateOrUpdateRequest = async () => {
    try {
      // Validate: for hourly requests spanning multiple days, check all days have the same schedule
      if (selectedVacationType?.is_hourly && newRequest.start_date && newRequest.end_date) {
        const workerId = !isAdmin && user ? user.id : Number(newRequest.worker_id);
        if (workerId) {
          const startStr = format(newRequest.start_date, 'yyyy-MM-dd');
          const endStr = format(newRequest.end_date, 'yyyy-MM-dd');
          const hoursRange = await getWorkHoursRange(workerId, startStr, endStr);
          if (!hoursRange.uniform && hoursRange.working_days > 1) {
            const detail = hoursRange.hours_per_day
              .map(d => `${d.date}: ${d.hours} h`)
              .join(', ');
            setBagError(
              `El rango seleccionado incluye días con jornadas distintas (${detail}). ` +
              `Para horas flexibles, selecciona un único día o un rango con la misma jornada.`
            );
            return;
          }
        }
      }

      // Validate: check remaining days in the selected bag against exact working days requested
      if (selectedVacationType && !selectedVacationType.is_hourly && newRequest.start_date && newRequest.end_date) {
        const workerId = !isAdmin && user ? user.id : Number(newRequest.worker_id);
        if (workerId) {
          const startStr = format(newRequest.start_date, 'yyyy-MM-dd');
          const endStr = format(newRequest.end_date, 'yyyy-MM-dd');

          // When editing, also get the original request's working days to add them back
          // (the backend counts the original request as used, so we need to compensate)
          const promises: Promise<any>[] = [
            getWorkersVacationSummary(newRequest.vacation_bag_year, newRequest.vacation_bag_year),
            countWorkingDays(workerId, startStr, endStr)
          ];
          if (editingRequest) {
            promises.push(countWorkingDays(workerId, editingRequest.start_date, editingRequest.end_date));
          }

          const [summaries, requestedDays, originalDays = 0] = await Promise.all(promises);
          const summary = summaries.find((s: WorkerVacationSummary) => s.worker_id === workerId);
          if (summary) {
            // For edits: add back the original days since they are already counted as used
            const effectiveRemaining = summary.remaining_vacation_days + (editingRequest ? originalDays : 0);
            if (requestedDays > effectiveRemaining) {
              setBagError(
                `No hay suficientes días en la bolsa de ${newRequest.vacation_bag_year}. ` +
                `Solicitas ${requestedDays} día${requestedDays !== 1 ? 's' : ''} laborable${requestedDays !== 1 ? 's' : ''} ` +
                `pero solo quedan ${effectiveRemaining}.`
              );
              return;
            }
          }
        }
      }
      setBagError('');

      const requestData: any = {
        worker_id: Number(newRequest.worker_id),
        vacation_type_id: Number(newRequest.vacation_type_id),
        start_date: format(newRequest.start_date!, 'yyyy-MM-dd'),
        end_date: format(newRequest.end_date!, 'yyyy-MM-dd'),
        hours_requested: newRequest.hours_requested ? Number(newRequest.hours_requested) : undefined,
        notes: newRequest.notes || undefined
      };

      // Include vacation bag year if we're in carryover period and it's different from request year
      if (isCarryoverPeriod() && newRequest.vacation_bag_year !== newRequest.start_date?.getFullYear()) {
        requestData.vacation_bag_year = newRequest.vacation_bag_year;
      }

      if (editingRequest) {
        await updateVacationRequest(editingRequest.id, requestData);
      } else {
        await createVacationRequest(requestData);
      }

      await fetchData();
      console.log('[DEBUG] fetchData done — ref after fetch:', previousYearSummariesRef.current);
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving request:', error);
      alert('Error al guardar la solicitud');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRequest(null);
    setDateError('');
    setBagError('');
    setDailyWorkHours(null);
    setBlockedDays(undefined);
    setNewRequest({
      worker_id: !isAdmin && user ? user.id.toString() : '',
      vacation_type_id: '',
      start_date: null,
      end_date: null,
      hours_requested: '',
      notes: '',
      vacation_bag_year: calcBagYear(null)
    });
  };

  const handleUpdateStatus = async (requestId: number, status: VacationStatus) => {
    try {
      const updatedRequest = await updateVacationRequest(requestId, { status });
      setRequests(requests.map(r => r.id === requestId ? updatedRequest : r));
    } catch (error) {
      console.error('Error updating request:', error);
    }
  };

  const selectedVacationType = vacationTypes.find(vt => vt.id === Number(newRequest.vacation_type_id));

  // Returns remaining days from previous year for a given workerId (null = no data)
  // Reads from ref so it always has the latest data, even inside stale closures
  const getPreviousYearRemainingDays = (workerId?: number): number | null => {
    const id = workerId ?? Number(newRequest.worker_id);
    if (!id) return null;
    const summary = previousYearSummariesRef.current.find(s => s.worker_id === id);
    console.log('[DEBUG] getPreviousYearRemainingDays — workerId:', id, '| ref data:', previousYearSummariesRef.current, '| summary:', summary);
    if (!summary) return null;
    return summary.remaining_vacation_days;
  };

  // Returns remaining days from current year for a given workerId (null = no data)
  const getCurrentYearRemainingDays = (workerId?: number): number | null => {
    const id = workerId ?? Number(newRequest.worker_id);
    if (!id) return null;
    const summary = currentYearSummariesRef.current.find((s: WorkerVacationSummary) => s.worker_id === id);
    if (!summary) return null;
    return summary.remaining_vacation_days;
  };

  // Returns remaining flexible hours from current year for a given workerId (null = no data)
  const getCurrentYearRemainingHours = (workerId?: number): number | null => {
    const id = workerId ?? Number(newRequest.worker_id);
    if (!id) return null;
    const summary = currentYearSummariesRef.current.find((s: WorkerVacationSummary) => s.worker_id === id);
    if (!summary) return null;
    return summary.remaining_flexible_hours;
  };

  // Calculates the correct bag year to auto-select for a given date and worker
  // Reads from refs so it always has the latest data, even inside stale closures
  const calcBagYear = (date: Date | null, workerId?: number): number => {
    const currentYear = new Date().getFullYear();
    if (!date) return currentYear;
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const today = new Date();
    const carryoverDeadline = new Date(currentYear, 2, 31);
    if (year === currentYear && month <= 3 && today <= carryoverDeadline && availableYearsRef.current.includes(currentYear - 1)) {
      const prevRemaining = getPreviousYearRemainingDays(workerId);
      const result = (prevRemaining !== null && prevRemaining > 0) ? currentYear - 1 : currentYear;
      console.log('[DEBUG] calcBagYear — prevRemaining:', prevRemaining, '| result:', result);
      return result;
    }
    return year;
  };

  // Check if we are in carryover period: current date is before March 31 of current year,
  // the selected dates are in Jan-Mar, the vacation type is full-day,
  // and there is actually a bag for the previous year
  const isCarryoverPeriod = () => {
    if (!newRequest.start_date || !selectedVacationType || selectedVacationType.is_hourly) {
      return false;
    }

    const today = new Date();
    const currentYear = today.getFullYear();
    // Only allow carryover if today is on or before March 31 of the current year
    const carryoverDeadline = new Date(currentYear, 2, 31); // March 31
    if (today > carryoverDeadline) {
      return false;
    }

    const startDate = newRequest.start_date;
    const startYear = startDate.getFullYear();
    const month = startDate.getMonth() + 1; // 1-based month

    // Only show bag selector if requesting days between Jan 1 - Mar 31 of the current year
    if (startYear !== currentYear || month > 3) {
      return false;
    }

    // Only show if there is actually a bag for the previous year
    return availableYears.includes(currentYear - 1);
  };

  return (
    <Box sx={{ p: 0 }}>
      <Box display="flex" justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'start' }} flexDirection={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 0 }} mb={{ xs: 3, sm: 6 }}>
        <Box>
          <Typography 
            variant="h5" 
            sx={{ 
              mb: 1,
              fontWeight: 700,
              color: '#001923',
              letterSpacing: '-0.01em',
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            Solicitudes de Vacaciones
          </Typography>
          <Typography 
            variant="body1" 
            className="descripcion-cyan"
            sx={{ 
              fontWeight: 300,
              lineHeight: 1.6
            }}
          >
            Gestión y seguimiento de peticiones
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenCreate()}
          sx={{
            backgroundColor: '#004254',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#003040'
            },
            alignSelf: { xs: 'stretch', sm: 'auto' }
          }}
        >
          Nueva Solicitud
        </Button>
      </Box>

      <Paper 
        className="vacationreqs-datagrid-paper"
        sx={{ 
          height: { xs: 'calc(100vh - 280px)', sm: 600 },
          minHeight: 400, 
          width: '100%',
          backgroundColor: '#FFFFFF',
          '&.vacationreqs-datagrid-paper .MuiDataGrid-root': {
            border: 'none',
            backgroundColor: '#FFFFFF'
          },
          '&.vacationreqs-datagrid-paper .MuiDataGrid-columnHeaders': {
            backgroundColor: '#00B0BD',
            borderBottom: '2px solid #00B0BD',
            color: '#001923'
          },
          '&.vacationreqs-datagrid-paper .MuiDataGrid-columnHeaderTitle': {
            color: '#001923',
            fontWeight: 600
          },
          '&.vacationreqs-datagrid-paper .MuiDataGrid-columnHeader': {
            padding: { xs: '0 8px', sm: '0 16px' }
          },
          '&.vacationreqs-datagrid-paper .MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
            display: { xs: 'none !important', sm: 'inline-flex' }
          },
          '&.vacationreqs-datagrid-paper .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer': {
            display: { xs: 'none !important', sm: 'inline-flex' }
          },
          '&.vacationreqs-datagrid-paper .MuiDataGrid-cell': {
            borderBottom: '1px solid #f0f0f0',
            color: '#001923'
          },
          '@media (max-width: 600px)': {
            '&.vacationreqs-datagrid-paper .MuiDataGrid-actionsCell': {
              gap: '0px'
            },
            '&.vacationreqs-datagrid-paper .MuiDataGrid-actionsCell .MuiButtonBase-root': {
              padding: '4px',
              minWidth: 'unset'
            }
          },
          '&.vacationreqs-datagrid-paper .MuiDataGrid-row': {
            backgroundColor: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#f5f5f5'
            }
          }
        }}>
        <DataGrid
          rows={requests}
          columns={columns}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 }
            }
          }}
          pageSizeOptions={[10, 25, 50]}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnMenu={isMobile}
        />
      </Paper>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {editingRequest ? 'Editar Solicitud' : 'Crear Nueva Solicitud'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {isAdmin && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Trabajador</InputLabel>
                <Select
                  value={newRequest.worker_id}
                  label="Trabajador"
                  onChange={(e) => {
                    const newWorkerId = Number(e.target.value);
                    setNewRequest({
                      ...newRequest,
                      worker_id: e.target.value as string,
                      vacation_bag_year: calcBagYear(newRequest.start_date, newWorkerId)
                    });
                  }}
                >
                  {workers.map((worker) => (
                    <MenuItem key={worker.id} value={worker.id}>
                      {worker.name} {worker.surname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Tipo de Vacación</InputLabel>
              <Select
                value={newRequest.vacation_type_id}
                label="Tipo de Vacación"
                onChange={(e) => setNewRequest({ ...newRequest, vacation_type_id: e.target.value as string })}
              >
                {vacationTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {selectedVacationType && !selectedVacationType.is_hourly && (
              <FormControl fullWidth margin="normal">
                <InputLabel>Bolsa de Días</InputLabel>
                <Select
                  value={newRequest.vacation_bag_year}
                  label="Bolsa de Días"
                  onChange={(e) => setNewRequest({ ...newRequest, vacation_bag_year: Number(e.target.value) })}
                  disabled={!isCarryoverPeriod()}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      fontWeight: 300
                    }
                  }}
                >
                  {(() => {
                    const currentYear = new Date().getFullYear();
                    const previousYear = currentYear - 1;

                    if (isCarryoverPeriod()) {
                      // Carryover period: show both bags, auto-selected, previous year may be exhausted
                      const prevRemaining = getPreviousYearRemainingDays();
                      const prevExhausted = prevRemaining !== null && prevRemaining <= 0;
                      const currRemaining = getCurrentYearRemainingDays();
                      return [
                        availableYears.includes(previousYear) && (
                          <MenuItem
                            key={previousYear}
                            value={previousYear}
                            disabled={prevExhausted}
                            sx={{ fontWeight: 300, color: prevExhausted ? 'text.disabled' : 'inherit' }}
                          >
                            Días {previousYear} (anterior){prevExhausted ? ' — agotada' : prevRemaining !== null ? ` — ${prevRemaining} días disponibles` : ''}
                          </MenuItem>
                        ),
                        <MenuItem key={currentYear} value={currentYear} sx={{ fontWeight: 300 }}>
                          Días {currentYear} (actual){currRemaining !== null ? ` — ${currRemaining} días disponibles` : ''}
                        </MenuItem>
                      ];
                    } else {
                      // Outside carryover: show only current year bag, disabled, with remaining days
                      const currRemaining = getCurrentYearRemainingDays();
                      return [
                        <MenuItem key={currentYear} value={currentYear} sx={{ fontWeight: 300 }}>
                          Días {currentYear} (actual){currRemaining !== null ? ` — ${currRemaining} días disponibles` : ''}
                        </MenuItem>
                      ];
                    }
                  })()}
                </Select>
              </FormControl>
            )}
            
            <DateRangePickerField
              startDate={newRequest.start_date}
              endDate={newRequest.end_date}
              minDate={new Date(new Date().getFullYear(), 0, 1)}
              maxDate={new Date(new Date().getFullYear() + 1, 2, 31)}
              error={!!dateError}
              helperText={dateError || undefined}
              blockedDays={blockedDays}
              onRangeChange={(start, end) => {
                // Validate range
                if (start && end && end < start) {
                  setDateError('La fecha de fin debe ser posterior a la fecha de inicio');
                } else {
                  setDateError('');
                }

                const wid = !isAdmin && user ? user.id : Number(newRequest.worker_id) || undefined;
                const newBagYear = calcBagYear(start, wid);

                setNewRequest(prev => ({
                  ...prev,
                  start_date: start,
                  end_date: end,
                  vacation_bag_year: newBagYear
                }));

                // If hourly type, fetch work hours for the selected start day
                if (selectedVacationType?.is_hourly && start && wid) {
                  getWorkerAvailableHours(wid, format(start, 'yyyy-MM-dd'))
                    .then(data => setDailyWorkHours(data.work_hours))
                    .catch(() => setDailyWorkHours(null));
                } else if (!selectedVacationType?.is_hourly) {
                  setDailyWorkHours(null);
                }
              }}
            />
            
            {selectedVacationType?.is_hourly && (
              <TextField
                fullWidth
                margin="normal"
                label={(() => {
                  const remaining = getCurrentYearRemainingHours();
                  if (remaining === null) return 'Horas Solicitadas';
                  return `Horas Solicitadas — ${remaining}h disponibles`;
                })()}
                type="number"
                value={newRequest.hours_requested}
                onChange={(e) => setNewRequest({ ...newRequest, hours_requested: e.target.value })}
                inputProps={{
                  min: 0.5,
                  max: dailyWorkHours ?? undefined,
                  step: 0.5
                }}
                helperText={dailyWorkHours !== null ? `Máximo ${dailyWorkHours} h (jornada laboral del día seleccionado)` : undefined}
              />
            )}
            
            <TextField
              fullWidth
              margin="normal"
              label="Notas (opcional)"
              multiline
              rows={3}
              value={newRequest.notes}
              onChange={(e) => setNewRequest({ ...newRequest, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 3, pb: 2 }}>
          {bagError && (
            <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
              {bagError}
            </Typography>
          )}
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button
              onClick={handleCloseDialog}
              variant="outlined"
              sx={{
                backgroundColor: '#FFFFFF',
                color: '#004254',
                borderColor: '#004254',
                textTransform: 'none',
                fontFamily: 'ForFutureSans, sans-serif',
                fontWeight: 700,
                borderRadius: 0,
                px: 3,
                '&:hover': { backgroundColor: '#f0f7f9', borderColor: '#004254' },
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateOrUpdateRequest}
              variant="contained"
              disabled={!!dateError}
              sx={{
                backgroundColor: '#004254',
                color: '#FFFFFF',
                textTransform: 'none',
                fontFamily: 'ForFutureSans, sans-serif',
                fontWeight: 700,
                borderRadius: 0,
                px: 3,
                '&:hover': { backgroundColor: '#003040' },
                '&.Mui-disabled': { backgroundColor: '#ccc', color: '#fff' },
              }}
            >
              {editingRequest ? 'Actualizar' : 'Crear Solicitud'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VacationRequests;